import { Turnstile } from "@marsidev/react-turnstile";
import { AsYouType, parsePhoneNumberFromString } from "libphonenumber-js";
import { useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Pressable,
  StyleSheet,
  TextInput,
  View,
} from "react-native";

import { GText } from "@/components/g-text";
import { colors } from "@/constants/colors";
import Bugsnag from "@/utils/bugsnag";
import { supabase } from "@/utils/supabase";

interface Props {
  embedded?: boolean;
  onClose: () => void;
  onVerified: () => void;
  visible: boolean;
}

type Step = "otp" | "phone";

export default function PhoneLogin({
  embedded,
  onClose,
  onVerified,
  visible,
}: Props) {
  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [turnstileToken, setTurnstileToken] = useState<null | string>(null);

  if (!visible) return null;

  function reset() {
    setStep("phone");
    setPhone("");
    setOtp("");
    setError(null);
    setLoading(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  const formattedPhone =
    phone.length > 0 ? new AsYouType("US").input(phone) : "";

  function parsedPhone() {
    return parsePhoneNumberFromString(phone, "US");
  }

  function fullPhone() {
    const parsed = parsedPhone();
    return parsed ? parsed.format("E.164") : "";
  }

  function isPhoneValid() {
    const parsed = parsedPhone();
    return parsed ? parsed.isValid() : false;
  }

  function handlePhoneChange(text: string) {
    const digits = text.replace(/\D/g, "");
    // User deleted a formatting char — drop a real digit instead
    if (digits === phone && text.length < formattedPhone.length) {
      setPhone(digits.slice(0, -1));
      return;
    }
    setPhone(digits);
  }

  async function sendCode() {
    setLoading(true);
    setError(null);

    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        options: {
          captchaToken: turnstileToken ?? undefined,
        },
        phone: fullPhone(),
      });

      setLoading(false);

      if (otpError) {
        Bugsnag.notify(new Error(`sendCode OTP error: ${otpError.message}`));
        setError(otpError.message);
        return;
      }

      setStep("otp");
    } catch (e) {
      Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
      setLoading(false);
      setError("Something went wrong. Please try again.");
    }
  }

  async function verifyCode() {
    setLoading(true);
    setError(null);

    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        phone: fullPhone(),
        token: otp,
        type: "sms",
      });

      setLoading(false);

      if (verifyError) {
        Bugsnag.notify(new Error(`verifyCode error: ${verifyError.message}`));
        setError(verifyError.message);
        return;
      }

      reset();
      onVerified();
    } catch (e) {
      Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
      setLoading(false);
      setError("Something went wrong. Please try again.");
    }
  }

  const content = (
    <View>
      {step === "phone" ? (
        <View>
          <TextInput
            autoComplete="tel"
            importantForAutofill="yes"
            keyboardAppearance="dark"
            keyboardType="phone-pad"
            onChangeText={handlePhoneChange}
            placeholder="enter your number"
            placeholderTextColor={colors.placeholder}
            returnKeyType="default"
            style={styles.pillInput}
            textContentType="telephoneNumber"
            value={formattedPhone}
          />
          {error && <GText style={styles.error}>{error}</GText>}
          <Turnstile
            onSuccess={setTurnstileToken}
            options={{ size: "invisible", theme: "dark" }}
            siteKey={process.env.EXPO_PUBLIC_TURNSTILE_SITE_KEY!}
          />
          {(isPhoneValid() && !turnstileToken) || loading ? (
            <View style={[styles.btn, styles.btnDisabled]}>
              <ActivityIndicator color={colors.black} />
            </View>
          ) : (
            <Pressable
              disabled={!isPhoneValid()}
              onPress={sendCode}
              style={[styles.btn, !isPhoneValid() && styles.btnDisabled]}
            >
              <GText style={styles.btnText}>send code</GText>
            </Pressable>
          )}
        </View>
      ) : (
        <View>
          <GText style={styles.subtext}>
            code sent to {parsedPhone()?.formatNational()}
          </GText>
          <TextInput
            autoComplete="one-time-code"
            keyboardAppearance="dark"
            keyboardType="number-pad"
            maxLength={6}
            onChangeText={(text) => setOtp(text.replace(/\D/g, ""))}
            placeholder="verification code"
            placeholderTextColor={colors.placeholder}
            returnKeyType="done"
            style={[styles.pillInput, otp.length > 0 && styles.otpSpacing]}
            value={otp}
          />
          {error && <GText style={styles.error}>{error}</GText>}
          {loading ? (
            <View style={[styles.btn, styles.btnDisabled]}>
              <ActivityIndicator color={colors.black} />
            </View>
          ) : (
            <Pressable
              disabled={otp.length !== 6}
              onPress={verifyCode}
              style={[styles.btn, otp.length !== 6 && styles.btnDisabled]}
            >
              <GText style={styles.btnText}>verify</GText>
            </Pressable>
          )}

          <View style={styles.linkRow}>
            <Pressable
              onPress={() => {
                setStep("phone");
                setOtp("");
                setError(null);
              }}
            >
              <GText style={styles.linkBtn}>change number</GText>
            </Pressable>
            <Pressable disabled={loading} onPress={sendCode}>
              <GText style={styles.linkBtn}>resend code</GText>
            </Pressable>
          </View>
        </View>
      )}
    </View>
  );

  if (embedded) return content;

  return (
    <Modal
      animationType="fade"
      onRequestClose={handleClose}
      transparent
      visible={visible}
    >
      <Pressable onPress={handleClose} style={styles.backdrop}>
        <Pressable onPress={() => {}} style={styles.modal}>
          {content}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    alignItems: "center",
    backgroundColor: colors.darkOverlay,
    flex: 1,
    justifyContent: "center",
  },
  btn: {
    alignItems: "center",
    backgroundColor: colors.white,
    borderRadius: 28,
    marginTop: 16,
    paddingVertical: 14,
    width: "100%",
  },
  btnDisabled: {
    opacity: 0.5,
  },
  btnText: {
    color: colors.black,
    fontSize: 16,
    fontWeight: "600",
  },
  error: {
    color: colors.red,
    fontSize: 14,
    marginTop: 8,
  },
  linkBtn: {
    color: colors.white,
    fontSize: 16,
  },
  linkRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 12,
  },
  modal: {
    backgroundColor: colors.modal,
    borderRadius: 16,
    maxWidth: 420,
    padding: 24,
    width: "100%",
  },
  otpSpacing: {
    letterSpacing: 8,
  },
  pillInput: {
    backgroundColor: colors.transparent,
    borderColor: colors.white,
    borderRadius: 44,
    borderWidth: 2,
    color: colors.white,
    fontSize: 18,
    fontWeight: "600",
    minHeight: 54,
    outlineColor: colors.white,
    paddingHorizontal: 20,
    paddingVertical: 14,
    textAlign: "center",
    width: "100%",
  },
  subtext: {
    color: colors.light,
    fontSize: 14,
    marginBottom: 12,
    textAlign: "center",
  },
});
